import { useState, useEffect, useCallback, useMemo } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Session-level cache of every dashboard the user opens. Persists for the
 * lifetime of the SPA so re-opening the Dashboard tab on the same project
 * is instant — no fetch, no loading screen, no "Aria is crafting" flicker.
 * The backend remains the source of truth; the cache is invalidated on
 * regenerate() / patch().
 */
const _DASHBOARD_CACHE = new Map(); // chatId → dashboard payload

/**
 * Encapsulates dashboard fetching, auto-generation, theme injection.
 * One hook per chat — caller passes the active chat_id.
 *
 * Returns:
 *   { status, dashboard, error, regenerate, patch }
 *   status: 'idle' | 'hydrating' | 'generating' | 'ready' | 'error'
 *
 * - 'hydrating' = silent background fetch when no cache yet (no loading UI).
 * - 'generating' = first-ever Aria generation (full loading UI).
 */
export function useDashboard(chatId) {
  // Hydrate from cache synchronously — avoids any loading flicker for
  // dashboards we've already loaded in this session.
  const cached = chatId ? _DASHBOARD_CACHE.get(chatId) : null;
  const [status, setStatus] = useState(cached ? 'ready' : 'idle');
  const [dashboard, setDashboard] = useState(cached || null);
  const [error, setError] = useState(null);

  const fetchDash = useCallback(async () => {
    if (!chatId) return;
    // Already hydrated from cache → just trust it. The backend persists the
    // dashboard so re-fetching every open would be wasted bandwidth.
    if (_DASHBOARD_CACHE.has(chatId)) {
      setDashboard(_DASHBOARD_CACHE.get(chatId));
      setStatus('ready');
      return;
    }
    setStatus('hydrating');
    setError(null);
    try {
      const r = await fetch(`${API}/api/dashboards/${chatId}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`fetch ${r.status}`);
      const j = await r.json();
      if (!j.exists) {
        // First time on this project — run Aria once. Subsequent opens hit
        // the cache and skip both this branch and the GET above.
        setStatus('generating');
        const g = await fetch(`${API}/api/dashboards/${chatId}/generate`, {
          method: 'POST', credentials: 'include',
        });
        if (!g.ok) throw new Error(`generate ${g.status}`);
        const gj = await g.json();
        _DASHBOARD_CACHE.set(chatId, gj);
        setDashboard(gj);
      } else {
        _DASHBOARD_CACHE.set(chatId, j);
        setDashboard(j);
      }
      setStatus('ready');
    } catch (e) {
      setError(String(e?.message || e));
      setStatus('error');
    }
  }, [chatId]);

  useEffect(() => { fetchDash(); }, [fetchDash]);

  const regenerate = useCallback(async () => {
    if (!chatId) return;
    _DASHBOARD_CACHE.delete(chatId);
    setStatus('generating');
    try {
      await fetch(`${API}/api/dashboards/${chatId}`, {
        method: 'DELETE', credentials: 'include',
      });
      const g = await fetch(`${API}/api/dashboards/${chatId}/generate`, {
        method: 'POST', credentials: 'include',
      });
      const gj = await g.json();
      _DASHBOARD_CACHE.set(chatId, gj);
      setDashboard(gj);
      setStatus('ready');
    } catch (e) {
      setError(String(e?.message || e));
      setStatus('error');
    }
  }, [chatId]);

  const patch = useCallback(async (body) => {
    if (!chatId) return;
    const r = await fetch(`${API}/api/dashboards/${chatId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      const j = await r.json();
      setDashboard(prev => {
        const next = { ...prev, ...j };
        _DASHBOARD_CACHE.set(chatId, next);
        return next;
      });
    }
  }, [chatId]);

  // Stable rows accessors. In Production mode the project isn't yet streaming
  // events to the cockpit, so we expose empty datasets — the UI shows clean
  // empty states ("Waiting for your first customer / order / …") instead of
  // leaking sample data into what should be a live operations view.
  const rows = useMemo(() => {
    const isLive = dashboard?.mode === 'live';
    if (isLive) {
      return {
        customers: [],
        products: [],
        orders: [],
        content: [],
        emails: [],
        analytics: {
          revenue_30d: 0,
          revenue_prev: 0,
          customers_30d: 0,
          customers_prev: 0,
          orders_30d: 0,
          orders_prev: 0,
          conversion: 0,
          conversion_prev: 0,
          revenue_series: new Array(30).fill(0),
          top_sources: [],
        },
      };
    }
    return {
      customers: dashboard?.tables?.customers?.rows || [],
      products:  dashboard?.tables?.products?.rows || [],
      orders:    dashboard?.tables?.orders?.rows || [],
      content:   dashboard?.tables?.content?.rows || [],
      emails:    dashboard?.tables?.emails?.rows || [],
      analytics: dashboard?.tables?.analytics?.rows?.[0] || {},
    };
  }, [dashboard]);

  return { status, dashboard, rows, error, regenerate, patch };
}
